import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { WithChildren } from "../helpers/with-children";
import { ApplicationError } from "../pages/application-error/applicationError";

import { MasterLayout } from "../pages/layouts";
import { SplashScreen } from "../pages/layouts/core/SplashScreen";
import CustomerTrucks from "../pages/configurations/customers/components/CustomerTrucks";
import Loader from "../pages/common/loader/loader";
import KaribuUtilityRedirect from "./KaribuUtilityRedirect";



const PrivateRoutes = () => {
  const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
  const Page401 = lazy(() => import("../pages/auth/components/401/page401"));
  const Page404 = lazy(() => import("../pages/auth/components/404/page404"));

  const Configurations = lazy(
    () => import("../pages/configurations/Configurations")
  );

  const Reporting = lazy(() => import("../pages/reporting/Reports"));

  const Payments = lazy(() => import("../pages/payments/components/Payments"));

  const Companies = lazy(
    () => import("../pages/configurations/companies/components/Companies")
  );

  const Accounts = lazy(
    () => import("../pages/configurations/accounts/components/Accounts")
  );

  const Locations = lazy(
    () => import("../pages/configurations/locations/components/Locations")
  );

  const Customers = lazy(
    () => import("../pages/configurations/customers/components/Customers")
  );
  const CustomerDetail = lazy(
    () => import("../pages/configurations/customers/components/CustomerDetail")
  );

  const LinkedAccounts = lazy(
    () => import("../pages/configurations/customers/components/LinkedAccounts")
  );

  const ContentManagement = lazy(
    () => import("../pages/content-management/ContentManagement")
  );
  const ProductsPage = lazy(
    () => import("../pages/content-management/components/Products/Products")
  );
  const PromotionsPage = lazy(
    () => import("../pages/content-management/components/Promotions/Promotions")
  );
  const NewsPage = lazy(
    () =>
      import(
        "../pages/content-management/components/NewsAndStories/NewsAndStories"
      )
  );
  const ContactsPage = lazy(
    () => import("../pages/content-management/components/Contact/Contacts")
  );
  const QuestionsPage = lazy(
    () => import("../pages/content-management/components/Questions/Questions")
  );

  const SalesVouchers = lazy(
    () => import("../pages/sales-vouchers/components/SalesVouchers")
  );
  const SalesOrdersLayout = lazy(
    () => import("../pages/sales-vouchers/components/SalesOrdersLayout")
  );
  const UserSalesVoucherDetails = lazy(
    () => import("../pages/sales-vouchers/components/UserSalesVoucherDetail")
  );

  const SalesOrderCancellations = lazy(
    () =>
      import(
        "../pages/sales-order-cancellations/SalesOrderCancellations"
      )
  );

  const SalesOrderCancellationDetail = lazy(
    () =>
      import(
        "../pages/sales-order-cancellations/SalesOrderCancellationDetail"
      )
  );

  const Planner = lazy(
    () => import("../pages/planner/Planner")
  );
  const BanksMasterData = lazy(
    () => import("../pages/configurations/banks/components/BanksMasterData")
  );
  const SettleMentBanks = lazy(
    () =>
      import(
        "../pages/configurations/setllement-banks/components/SettleMentBanks"
      )
  );
  const ProductConfiguration = lazy(
    () =>
      import(
        "../pages/configurations/products-configuration/components/ProductConfiguration"
      )
  );
  const ProductLocation = lazy(
    () =>
      import(
        "../pages/configurations/products-location/components/ProductLocation"
      )
  );

  const CustomerDestinationConfiguration = lazy(
    () =>
      import(
        "../pages/configurations/destinations/components/CustomerDestinations"
      )
  );

  const Destination = lazy(
    () => import("../pages/configurations/destination/components/Destination")
  );
  const VehicleRequests = lazy(
    () =>
      import(
        "../pages/configurations/transport-requests/components/VehicleRequests"
      )
  );
  const DriverRequests = lazy(
    () =>
      import(
        "../pages/configurations/transport-requests/components/DriverRequests"
      )
  );
  const ManualPayment = lazy(
    () => import("../pages/manual-payments/components/ManualPayment")
  );

  const SalesVouchersReport = lazy(
    () => import("../pages/reporting/components/salesvouchers/SalesVouchers")
  );
  const SalesOrdersReport = lazy(
    () => import("../pages/reporting/components/salesorders/SalesOrders")
  );

  const DfsManagementReport = lazy(
    () => import("../pages/reporting/components/dfs-management/DFSManagement")
  );

  const ManualPayments = lazy(
    () => import("../pages/reporting/components/manual-payments/ManualPayments")
  );
  const EPayments = lazy(
    () => import("../pages/reporting/components/e-payments/Epayments")
  );
  const ManualPaymentApprovalTime = lazy(
    () =>
      import(
        "../pages/reporting/components/manual-payment-approval-time/ManualPaymentApprovalTime"
      )
  );
  const ActiveUserSales = lazy(
    () =>
      import(
        "../pages/reporting/components/active-users-sales/ActiveUserSales"
      )
  );
  const ActiveUserOrders = lazy(
    () =>
      import(
        "../pages/reporting/components/active-user-orders/ActiveUserOrders"
      )
  );
  const Delivery = lazy(
    () => import("../pages/reporting/components/delivery/Delivery")
  );
  const Users = lazy(() => import("../pages/reporting/components/users/Users"));

  const BankGuarantee = lazy(() => import("../pages/bank-guarantee-dfs/Index"));

  const DFSChequeTracker = lazy(
    () => import("../pages/bank-guarantee-dfs/dfs-cheque/DFSChequeTracker")
  );
  const CustomerWithBankGuarantee = lazy(
    () =>
      import(
        "../pages/bank-guarantee-dfs/customer-with-guarantee/components/CustomerWithBankGuarantee"
      )
  );

  const BankGuaranteeCustomersView = lazy(
    () =>
      import(
        "../pages/bank-guarantee-dfs/customer-with-guarantee/components/ViewCustomer"
      )
  );

  const Logistics = lazy(() => import("../pages/logistics/Logistics"));
  const LogisticsSalesVouchers = lazy(
    () => import("../pages/logistics/components/LogisticsSalesVouchers")
  );
  const LogisticsChangeRequests = lazy(
    () => import("../pages/logistics/components/LogisticsChangeRequests")
  );
  const LogisticsDefaultTabRedirect = lazy(
    () => import("../pages/logistics/components/LogisticsDefaultTabRedirect")
  );
  const LogisticsSalesVoucherDetail = lazy(
    () => import("../pages/logistics/components/LogisticsSalesVoucherDetail")
  );
  const BasketOrders = lazy(
    () => import("../pages/basket-orders/components/BasketOrders")
  );
  const BasketOrderDetail = lazy(
    () => import("../pages/basket-orders/components/BasketOrderDetail")
  );

  // Karibu Utility Pages
  const KaribuUtility = lazy(
    () => import("../pages/karibu-utility/KaribuUtility")
  );
  const QuotationBodListPage = lazy(
    () => import("../pages/karibu-utility/components/QuotationBodListPage")
  );
  const SalesOrderBodListPage = lazy(
    () => import("../pages/karibu-utility/components/SalesOrderBodListPage")
  );
  const QuotationBodDetailPageWrapper = lazy(
    () => import("../pages/karibu-utility/components/QuotationBodDetailPageWrapper")
  );
  const SalesOrderBodDetailPageWrapper = lazy(
    () => import("../pages/karibu-utility/components/SalesOrderBodDetailPageWrapper")
  );
  const PaymentBodPage = lazy(
    () => import("../pages/karibu-utility/components/PaymentBodPage")
  );
  const AddAdminToLocations = lazy(
    () => import("../pages/karibu-utility/components/AddAdminToLocations")
  );

  return (
    <KaribuUtilityRedirect>
      <Routes>
        <Route path="application-error" element={<ApplicationError />} />
        <Route element={<MasterLayout />}>
          <Route path="" element={<Navigate to="/landing" />} />
          <Route
            path="landing"
            element={
              <SuspensedView>
                <Dashboard />
              </SuspensedView>
            }
          />
          <Route
            path="401"
            element={
              <SuspensedView>
                <Page401 />
              </SuspensedView>
            }
          />
          <Route
            path="/content-management"
            element={
              <SuspensedView>
                <ContentManagement />
              </SuspensedView>
            }
          >
            <Route index element={<Navigate to="products" />} />
            <Route
              path="products"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <ProductsPage />
                </Suspense>
              }
            />
            <Route
              path="promotions"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <PromotionsPage />
                </Suspense>
              }
            />
            <Route
              path="news-and-stories"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <NewsPage />
                </Suspense>
              }
            />
            <Route
              path="contacts"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <ContactsPage />
                </Suspense>
              }
            />
            <Route
              path="questions"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <QuestionsPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="404"
            element={
              <SuspensedView>
                <Page404 />
              </SuspensedView>
            }
          />
          <Route
            path="reports"
            element={
              <SuspensedView>
                <Reporting />
              </SuspensedView>
            }
          />
          <Route
            path="reports/sales-vouchers"
            element={
              <SuspensedView>
                <SalesVouchersReport />
              </SuspensedView>
            }
          />
          <Route
            path="reports/manual-payments"
            element={
              <SuspensedView>
                <ManualPayments />
              </SuspensedView>
            }
          />
          <Route
            path="reports/e-payments"
            element={
              <SuspensedView>
                <EPayments />
              </SuspensedView>
            }
          />
          <Route
            path="reports/manual-payment-approval-time"
            element={
              <SuspensedView>
                <ManualPaymentApprovalTime />
              </SuspensedView>
            }
          />
          <Route
            path="bank-guarantee"
            element={
              <SuspensedView>
                <BankGuarantee />
              </SuspensedView>
            }
          />
          <Route
            path="bank-guarantee/dfs-cheque-tracker"
            element={
              <SuspensedView>
                <DFSChequeTracker />
              </SuspensedView>
            }
          />
          <Route
            path="bank-guarantee/customers-with-bank-guarantee"
            element={
              <SuspensedView>
                <CustomerWithBankGuarantee />
              </SuspensedView>
            }
          />
          <Route
            path="reports/delivery"
            element={
              <SuspensedView>
                <Delivery />
              </SuspensedView>
            }
          />
          <Route
            path="reports/users"
            element={
              <SuspensedView>
                <Users />
              </SuspensedView>
            }
          />
          <Route
            path="configurations"
            element={
              <SuspensedView>
                <Configurations />
              </SuspensedView>
            }
          />
          <Route
            path="payments"
            element={
              <SuspensedView>
                <Payments />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/companies"
            element={
              <SuspensedView>
                <Companies />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/accounts"
            element={
              <SuspensedView>
                <Accounts />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/locations"
            element={
              <SuspensedView>
                <Locations />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/banks"
            element={
              <SuspensedView>
                <BanksMasterData />
              </SuspensedView>
            }
          />

          <Route
            path="configurations/settlement-banks"
            element={
              <SuspensedView>
                <SettleMentBanks />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/products-configuration"
            element={
              <SuspensedView>
                <ProductConfiguration />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/destination-configuration/:id?"
            element={
              <SuspensedView>
                <CustomerDestinationConfiguration />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/vehicle-requests"
            element={
              <SuspensedView>
                <VehicleRequests />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/driver-requests"
            element={
              <SuspensedView>
                <DriverRequests />
              </SuspensedView>
            }
          />
          <Route
            path="manual-payments/:paymentId"
            element={
              <SuspensedView>
                <ManualPayment />
              </SuspensedView>
            }
          />
          <Route
            path="orders"
            element={
              <SuspensedView>
                <SalesOrdersLayout />
              </SuspensedView>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <SalesVouchers />
                </Suspense>
              }
            />
            <Route
              path="approvals"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <SalesOrderCancellations />
                </Suspense>
              }
            />
          </Route>

          <Route
            path={"orders-details/:id"}
            element={
              <SuspensedView>
                <UserSalesVoucherDetails />
              </SuspensedView>
            }
          />
          <Route
            path={"orders-cancellation-details/:id"}
            element={
              <SuspensedView>
                <SalesOrderCancellationDetail />
              </SuspensedView>
            }
          />
          <Route
            path="logistics"
            element={
              <SuspensedView>
                <Logistics />
              </SuspensedView>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <LogisticsDefaultTabRedirect />
                </Suspense>
              }
            />
            <Route
              path="approvals"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <LogisticsChangeRequests />
                </Suspense>
              }
            />
            <Route
              path="sales-vouchers"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <LogisticsSalesVouchers />
                </Suspense>
              }
            />
            <Route
              path="sales-vouchers/:id"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <LogisticsSalesVoucherDetail />
                </Suspense>
              }
            />
            <Route
              path="basket-orders"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <BasketOrders />
                </Suspense>
              }
            />
            <Route
              path="basket-orders/:id"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <BasketOrderDetail />
                </Suspense>
              }
            />
            <Route
              path="planner"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <Planner />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="planner"
            element={
              <SuspensedView>
                <Planner />
              </SuspensedView>
            }
          />

          <Route
            path="configurations/customers"
            element={
              <SuspensedView>
                <Customers />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/customer/:id"
            element={
              <SuspensedView>
                <CustomerDetail />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/customer/trucks/:id"
            element={
              <SuspensedView>
                <CustomerTrucks />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/linked-accounts/:id"
            element={
              <SuspensedView>
                <LinkedAccounts />
              </SuspensedView>
            }
          />

          <Route
            path="content-management"
            element={
              <SuspensedView>
                <ContentManagement />
              </SuspensedView>
            }
          />

          <Route
            path="reports/sales-orders"
            element={
              <SuspensedView>
                <SalesOrdersReport />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/products-location"
            element={
              <SuspensedView>
                <ProductLocation />
              </SuspensedView>
            }
          />
          <Route
            path="reports/active-user-sales"
            element={
              <SuspensedView>
                <ActiveUserSales />
              </SuspensedView>
            }
          />
          <Route
            path="reports/active-user-orders"
            element={
              <SuspensedView>
                <ActiveUserOrders />
              </SuspensedView>
            }
          />
          <Route
            path="reports/dfs-management"
            element={
              <SuspensedView>
                <DfsManagementReport />
              </SuspensedView>
            }
          />
          <Route
            path="configurations/destination"
            element={
              <SuspensedView>
                <Destination />
              </SuspensedView>
            }
          />
          <Route
            path="bank-guarantee"
            element={
              <SuspensedView>
                <BankGuarantee />
              </SuspensedView>
            }
          />
          <Route
            path="bank-guarantee/customers-with-bank-guarantee/:id"
            element={
              <SuspensedView>
                <BankGuaranteeCustomersView />
              </SuspensedView>
            }
          />
          {/* Karibu Utility Routes */}
          <Route
            path="/karibu-utility"
            element={
              <SuspensedView>
                <KaribuUtility />
              </SuspensedView>
            }
          >
            <Route index element={<Navigate to="quotation-bod" />} />
            <Route
              path="quotation-bod"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <QuotationBodListPage />
                </Suspense>
              }
            />
            <Route
              path="quotation-bod/:salesVoucherId"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <QuotationBodDetailPageWrapper />
                </Suspense>
              }
            />
            <Route
              path="sales-order-bod"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <SalesOrderBodListPage />
                </Suspense>
              }
            />
            <Route
              path="sales-order-bod/:salesVoucherId"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <SalesOrderBodDetailPageWrapper />
                </Suspense>
              }
            />
            <Route
              path="payment-bod"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <PaymentBodPage />
                </Suspense>
              }
            />
            <Route
              path="add-admin-to-locations"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <AddAdminToLocations />
                </Suspense>
              }
            />
            <Route
              path="add-admin-to-locations/:userId"
              element={
                <Suspense fallback={<Loader loaderSize={10} />}>
                  <AddAdminToLocations />
                </Suspense>
              }
            />
          </Route>
          { }
          <Route path="/login" element={<Navigate to="/landing" />} />
          <Route path="*" element={<Navigate to="/404" />} />
          <Route path="*" element={<Navigate to="/landing" />} />
        </Route>
      </Routes>
    </KaribuUtilityRedirect>
  );
};

const SuspensedView = ({ children }: WithChildren) => {
  const baseColor = "red";
  const newLocal = {
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  };
  TopBarProgress.config(newLocal);
  return <Suspense fallback={<SplashScreen />}>{children}</Suspense>;
};

export { PrivateRoutes };
